<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { useField, useForm } from 'vee-validate'
import { object, array, date, string } from 'yup'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import {
  DCard,
  DBtn,
  DTextField,
  DSelectFieldSearch,
  DMultiselect,
  DModal,
  DLoading,
  DEmployeesFilter,
  FilterLinesIcon,
  Users01Icon,
  DToggleField
} from 'front-library'
import {
  convertArrayToString,
  ParametersCustomizedPayroll,
  ReportCustomizedPayroll,
  generateCustomizedPayroll,
  getPayroll,
  getProcessCountry,
  getEmployeesReport,
  getConcepts,
  getReportRequest,
  getProcessedPayrolls,
  filterProcessSelectReports,
  getCurrencies
} from '@/app/core/services/reports'
usePayrollChange(() => back())

/* store, router, notifications */
const storePayroll = usePayrollStore()
const store = useUserStore()
const router = useRouter()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Propiedades Computadas */
const filterOptions = computed(() => {
  return (items: any, search: any) =>
    (items = items.filter(
      (item: any) =>
        item.name.toLowerCase().indexOf(search.toString().toLowerCase()) > -1
    ))
})

/* Datos Listas */
const itemsPayrolls = ref<any[]>([])
const itemsConcepts = ref<any[]>([])
const itemsCurrencies = ref<any[]>([])
const searchCurrencies = ref<string>('')
const allConcepts = ref<any[]>([])
const allIdsConceptsSelected = ref<any[]>([])
const itemsEmployees = ref<string[]>([])
const itemsProcesses = ref<string[]>([])
const itemsStatusPayroll = ref([
  { id: 'PRENOMINA', name: 'PRENOMINA' },
  { id: 'CLOSED', name: 'NOMINA' }
])

const itemsColumns = ref([
  { id: 'companyName', name: t('reports.columns.companyName') },
  { id: 'taxId', name: t('reports.columns.taxId') },
  { id: 'department', name: t('reports.columns.department') },
  { id: 'sucursal', name: t('reports.columns.sucursal') },
  {
    id: 'employeeCode',
    name: t('reports.columns.employeeCode')
  },
  {
    id: 'employeeName',
    name: t('reports.columns.nameEmployee')
  },
  { id: 'identification', name: t('reports.columns.id') },
  { id: 'gender', name: t('reports.columns.gender') },
  { id: 'worldwide', name: t('reports.columns.worldwide') },
  {
    id: 'costCentersCodes',
    name: t('reports.columns.costCenterCode')
  },
  {
    id: 'costCentersNames',
    name: t('reports.columns.costCenterName')
  },
  {
    id: 'joiningDate',
    name: t('reports.columns.dateAdmission')
  },
  {
    id: 'leaveDate',
    name: t('reports.columns.departureDate')
  },
  { id: 'salary', name: t('reports.columns.baseSalary') },
  { id: 'position', name: t('reports.columns.position') },
  { id: 'direction', name: t('reports.columns.address') },
  {
    id: 'socialSecurity',
    name: t('reports.columns.socialSecurityNumber')
  }
])
const searchStatus = ref('')
const urlFile = ref(null)
const refresh = ref()

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  payrolls: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  processes: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  startDate: date().required(t('form.valid.required')),
  endDate: date().when('startDate', (startDate: any, schema) => {
    if (startDate[0] !== undefined) {
      return schema
        .min(
          startDate,
          `${t('form.valid.startDateMin')} ${dayjs(startDate).format(
            'DD/MM/YYYY'
          )}`
        )
        .required(t('form.valid.required'))
    }
    return schema.required(t('form.valid.required'))
  }),
  status: string().required(t('form.valid.required')),
  currencyId: string().required(t('form.valid.required')),
  concepts: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required')),
  employees: array()
    .min(1, t('form.valid.employeeSelectionRequired'))
    .required(t('form.valid.fieldRequired', { field: t('fields.employees') })),
  columns: array()
    .min(1, t('form.valid.minArray'))
    .required(t('form.valid.required'))
})

/* Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm<ParametersCustomizedPayroll>({
  initialValues: {
    showQuantity: true,
    showAmount: true,
    showCodes: false,
    columns: []
  },
  validationSchema
})

/* Definicion campos del formulario */
const { value: payrolls } = useField<string[]>(ref('payrolls'))
const { value: processes } = useField<string[]>(ref('processes'))
const { value: startDate } = useField<string | undefined>('startDate')
const { value: endDate } = useField<string | undefined>('endDate')
const { value: status } = useField<string>(ref('status'))
const { value: concepts } = useField<string[]>(ref('concepts'))
const { value: currencyId } = useField<string>(ref('currencyId'))
const { value: employees } = useField<any[]>(ref('employees'))
const { value: columns } = useField<string[]>(ref('columns'))
const { value: showQuantity } = useField<boolean>(ref('showQuantity'))
const { value: showAmount } = useField<boolean>(ref('showAmount'))
const { value: showCodes } = useField<boolean>(ref('showCodes'))

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values: any) => {
  values.concepts = allIdsConceptsSelected.value
  const path = route.params.code as string
  const decodeCode = atob(path)
  const createReport: ReportCustomizedPayroll = {
    code: decodeCode,
    parameters: values
  }
  void generateReport(createReport)
})

/* Crear solicitud del reporte */
const generateReport = async (values: any) => {
  loading.value = true
  messageLoading.value = t('loading.report')
  try {
    const response = await generateCustomizedPayroll(values)
    reportRequest(response)
  } catch (error: any) {
    showError(error)
  }
}

/* Mostrar estado del la solicitud del reporte generado */
const reportRequest = (id: string) => {
  urlFile.value = null
  refresh.value = setInterval(async () => {
    try {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModal.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    } catch (error: any) {
      showError(error)
    }
  }, 5000)
}

/* Listar nóminas procesadas */
const getListProcessedPayroll = async () => {
  if (startDate.value === '') startDate.value = undefined
  if (endDate.value === '') endDate.value = undefined
  let _dateIni = startDate.value
  let _dateEnd = endDate.value
  let _employees: any = []
  itemsEmployees.value = []
  employees.value = []
  try {
    if (
      processes.value != undefined &&
      _dateIni != undefined &&
      _dateEnd != undefined &&
      status.value &&
      payrolls.value?.length > 0
    ) {
      loading.value = true
      messageLoading.value = t('paymentReceipts.loading.payroll')
      let strProcesses = await convertArrayToString(processes.value)
      let strPayrolls = await convertArrayToString(payrolls.value)
      let _ProcessedPayroll = await getProcessedPayrolls(
        strPayrolls,
        strProcesses,
        _dateIni,
        _dateEnd,
        status.value
      )
      loading.value = false
      _employees = validateDataProcessedPayroll(_ProcessedPayroll)
      const strEmployees = await convertArrayToString(_employees)
      getEmployees(strEmployees)
    }
  } catch (error: any) {
    showError(error)
  }
}

/* Validar nomina procesada */
const validateDataProcessedPayroll = (data: any) => {
  const dataFilters = {
    processedPayroll: data,
    payrolls: payrolls.value,
    process: processes.value,
    status: status.value
  }
  return filterProcessSelectReports(dataFilters)
}

/* Listar empleados */
const getEmployees = async (employeesIds: string) => {
  loading.value = true
  messageLoading.value = t('loading.employee')
  try {
    if (employeesIds !== '') {
      itemsEmployees.value = await getEmployeesReport('', '', employeesIds)
      employees.value = itemsEmployees.value.map((_: any) => _.id)
    } else {
      notifications.addNotification({
        title: t('reports.notification.noEmployeesFound'),
        text: t('reports.notification.noEmployees'),
        type: 'warning',
        time: 10000
      })
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Listar conceptos */
const listConcepts = async () => {
  loading.value = true
  messageLoading.value = t('loading.concepts')
  concepts.value = []
  itemsConcepts.value = []
  try {
    if (payrolls.value?.length > 0) {
      const strPayrolls = await convertArrayToString(payrolls.value)
      allConcepts.value = await getConcepts(strPayrolls, 'customizedPayroll')
      itemsConcepts.value = [
        ...new Map(
          allConcepts.value.map((item: any) => [item['code'], item])
        ).values()
      ]
      if (itemsConcepts.value.length === 0) {
        notifications.addNotification({
          title: t('reports.notification.noConceptsFound'),
          text: t('reports.notification.noConcepts'),
          type: 'warning',
          time: 10000
        })
      }
      concepts.value = itemsConcepts.value.map((_: any) => _.code)
      await filterCodeConcepts(concepts.value)
    }
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

/* Filtrar codigos de conceptos */
const filterCodeConcepts = async (concepts: any) => {
  allIdsConceptsSelected.value = allConcepts.value
  allIdsConceptsSelected.value = allIdsConceptsSelected.value
    .filter(item => concepts.indexOf(item.code) > -1)
    .map(_ => _.id)
}

/* Mostrar notificación de error */
const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: error.message,
    text: error.response?.data?.message || '',
    type: 'error',
    time: 10000
  })
}

/* Cerrar modal */
const closeModal = () => {
  showModal.value = false
}

/* Volver a la lista de reportes */
const back = () => {
  void router.push({ name: 'MainReports' })
}

onMounted(async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const promisePayrolls = new Promise(resolve => {
      resolve(getPayroll(store.user?.id as string))
    })
    const promiseProcess = new Promise(resolve => {
      resolve(getProcessCountry())
    })
    const promiseCurrencies = new Promise(resolve => {
      resolve(getCurrencies())
    })

    Promise.all([promisePayrolls, promiseProcess, promiseCurrencies]).then(
      async (results: any) => {
        itemsPayrolls.value = await results[0]
        payrolls.value = itemsPayrolls.value
          .filter((_: any) => _.id === storePayroll.payroll?.id)
          .map((_: any) => _.id)
        itemsProcesses.value = await results[1]
        itemsCurrencies.value = await results[2]
        await listConcepts()
        itemsColumns.value.forEach((item: any) => {
          const excepteds = ['sucursal','companyName','taxId']
          if (!excepteds.includes(item.id)) {
            columns.value.push(item.id)
          }
        })
      }
    )
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <div class="h-full px-5 pb-5">
    <DLoading v-show="loading" :message="messageLoading" />
    <DModal
      :open="showModal"
      :typeAlert="typeAlertModal"
      :title="titleModal"
      :description="descriptionModal"
      @closeModal="closeModal">
      <template v-slot:description v-if="urlFile != null">
        <a
          class="underline tracking-widest text-blue-600"
          :href="urlFile"
          target="_blank"
          v-html="t('reports.notification.viewFile')" />
      </template>
    </DModal>
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('reports.customizedPayroll')" />
      </div>
    </div>
    <form class="scroll overflow-auto" style="height: calc(100% - 10rem)">
      <div class="flex flex-col gap-4">
        <DCard :footer="true">
          <div class="grid grid-cols-4">
            <div class="col-span-4 lg:col-span-3 sm:pb-5 lg:pb-0 px-3">
              <div class="grid grid-cols-6 gap-4">
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.payrolls')"
                    v-model="payrolls"
                    :headerTitle="t('reports.fields.payrolls')"
                    :required="true"
                    :options="itemsPayrolls"
                    :error="errors.payrolls"
                    @change="listConcepts(), getListProcessedPayroll()" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.processes')"
                    v-model="processes"
                    :headerTitle="t('reports.fields.processes')"
                    :required="true"
                    :options="itemsProcesses"
                    :error="errors.processes"
                    @change="getListProcessedPayroll" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.startDate')"
                    v-model="startDate"
                    :required="true"
                    :error="errors.startDate"
                    @change="getListProcessedPayroll" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DTextField
                    type="date"
                    :label="t('reports.fields.endDate')"
                    v-model="endDate"
                    :required="true"
                    :error="errors.endDate"
                    @change="getListProcessedPayroll" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.state')"
                    v-model="status"
                    :required="true"
                    :error="errors.status"
                    :options="filterOptions(itemsStatusPayroll, searchStatus)"
                    @filter="searchStatus = $event"
                    @change="getListProcessedPayroll" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DSelectFieldSearch
                    :label="t('reports.fields.generationCurrency')"
                    v-model="currencyId"
                    :required="true"
                    :options="filterOptions(itemsCurrencies, searchCurrencies)"
                    @filter="searchCurrencies = $event"
                    :error="errors.currencyId">
                  </DSelectFieldSearch>
                </div>
                <hr class="col-span-6" />
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.concepts')"
                    v-model="concepts"
                    :value-key="'code'"
                    :headerTitle="t('reports.fields.concepts')"
                    :required="true"
                    :options="itemsConcepts"
                    :error="errors.concepts"
                    @change="filterCodeConcepts(concepts)" />
                </div>
                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                  <DMultiselect
                    :label="t('reports.fields.columns')"
                    v-model="columns"
                    :required="true"
                    :headerTitle="t('reports.fields.columns')"
                    :options="itemsColumns"
                    :error="errors.columns" />
                </div>
                <div class="col-span-2"></div>
                <div class="col-span-2">
                  <DToggleField
                    v-model="showQuantity"
                    :label="t('reports.fields.showQuantity')" />
                </div>
                <div class="col-span-2">
                  <DToggleField
                    v-model="showAmount"
                    :label="t('reports.fields.showAmount')" />
                </div>
                <div class="col-span-2">
                  <DToggleField
                    v-model="showCodes"
                    :label="t('reports.fields.showCode')" />
                </div>
              </div>
            </div>
            <div
              class="flex flex-col col-span-4 lg:col-span-1 border-0 border-t-2 lg:border-l-4 p-2 lg:border-t-0">
              <div class="pl-4 pr-4 lg:pr-0 flex justify-between">
                <h3
                  class="text-md font-medium leading-6 text-gray-900"
                  v-html="t('fields.employees')" />
                <h3
                  class="text-md font-medium leading-6 text-gray-900 flex items-center">
                  <DEmployeesFilter
                    :items="itemsEmployees"
                    v-model="employees" />
                  <FilterLinesIcon class="w-8" />
                </h3>
              </div>
              <div
                class="flex justify-center items-center p-5 sm:gap-4 lg:gap-3 xl:gap-4">
                <Users01Icon class="w-20 h-20" />
                <h1 class="text-5xl font-medium leading-6 text-gray-900">
                  {{
                    `${
                      employees?.length === undefined ? 0 : employees?.length
                    }/${itemsEmployees?.length}`
                  }}
                </h1>
              </div>
              <div class="text-xs text-red-500 self-center">
                {{ errors.employees }}
              </div>
            </div>
          </div>
          <template v-slot:footer>
            <div class="flex justify-end gap-2">
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-5 py-2 font-semibold"
                v-html="t('form.button.back')" />
              <DBtn
                color="excel"
                class="px-5 py-2 font-semibold"
                @click.prevent="onSubmit()"
                v-html="t('form.button.excel')" />
            </div>
          </template>
        </DCard>
      </div>
    </form>
  </div>
</template>
